import {
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
} from '@angular/core';

import {
  ActivatedRoute,
  RouterLink,
} from '@angular/router';

import { CommonModule } from '@angular/common';

import { ActivityService } from '../../services/activity.service';
import {
  Activity,
  ActivityParticipant,
} from '../../interfaces/activity.interfaces';

import { StudentService } from '../../services/student.service';
import { Student } from '../../interfaces/student.interfaces';
import { FolioPillComponent } from '../ui/folio-pill/folio-pill.component';
import { FolioService } from '../../services/folio.service';

type Performance =
  | 'very_bad'
  | 'bad'
  | 'good'
  | 'very_good'
  | 'excellent';

@Component({
  selector: 'app-activity-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FolioPillComponent,
  ],
  templateUrl: './activity-detail.html',
  styleUrl: './activity-detail.scss',
})


export class ActivityDetail implements OnInit {

  private route = inject(ActivatedRoute);
  private activityService = inject(ActivityService);
  private studentService = inject(StudentService);
  private cdr = inject(ChangeDetectorRef);
  protected readonly folioService = inject(FolioService);

  activity: Activity | null = null;

  isLoading = true;

  activityId = '';

  participants: ActivityParticipant[] = [];

students: Student[] = [];

registeredStudents: ActivityParticipant[] = [];

  /*
   * Aquí guardamos temporalmente las horas
   * que el administrador escriba para cada estudiante.
   *
   * Ejemplo:
   * {
   *   'studentId1': 4,
   *   'studentId2': 6
   * }
   */
  validatedHoursInput: Record<string, number | null> = {};
  performanceInput: Record<string, string | null> = {};
  performanceCommentInput: Record<string, string> = {};
  studentToValidate: ActivityParticipant | null = null;
  hoursToConfirm: number | null = null;
  showValidateConfirmation = false;
  isValidating = false;


  ngOnInit(): void {

    this.activityId =
      this.route.snapshot.paramMap.get('id') ?? '';

    console.log(
      'ACTIVITY DETAIL ID:',
      this.activityId,
    );

    if (!this.activityId) {
      this.isLoading = false;
      return;
    }

    this.activityService
      .getActivityById(this.activityId)
      .then((activity) => {

        this.activity = activity;

        console.log(
          'ACTIVITY DETAIL DATA:',
          activity,
        );

        this.loadRegisteredStudents();

        this.isLoading = false;

        this.cdr.detectChanges();

      })
      .catch((error: unknown) => {

        console.error(
          'ERROR CARGANDO ACTIVIDAD:',
          error,
        );

        this.isLoading = false;

        this.cdr.detectChanges();

      });
  }


  /*
   * Cambia únicamente el estado de asistencia.
   *
   * Aquí NO validamos horas.
   */
  updateStatus(
    studentId: string,
    status: 'attended' | 'absent',
  ): void {

    console.log('🟢 CLICK ASISTENCIA');
    console.log('🟢 STUDENT ID:', studentId);
    console.log('🟢 STATUS:', status);

    if (!this.activityId) {
      return;
    }

    this.activityService
      .updateParticipationStatus(
        this.activityId,
        studentId,
        status,
      )
      .subscribe({

        next: () => {

          console.log(
            'STATUS ACTUALIZADO:',
            studentId,
            status,
          );

          /*
           * Si el administrador marca que el
           * estudiante no asistió, limpiamos
           * el valor temporal de horas.
           */
          if (status === 'absent') {
            this.validatedHoursInput[studentId] = null;
          }

          this.loadRegisteredStudents();
        },

        error: (error: unknown) => {

          console.error(
            'ERROR ACTUALIZANDO STATUS:',
            error,
          );

        },

      });
  }


  /*
   * Valida una cantidad específica de horas.
   *
   * Ya NO toma automáticamente
   * activity.valueInHours.
   *
   * El administrador decide cuántas horas
   * acreditar.
   */
  confirmValidateHours(): void {
    if (
      !this.studentToValidate ||
      this.hoursToConfirm === null ||
      this.hoursToConfirm === undefined ||
      this.isValidating ||
      !this.activityId
    ) {
      return;
    }
  
    const participant = this.studentToValidate;
    const hours = this.hoursToConfirm;
  
    const remainingHours =
      this.getRemainingHours(participant)
  
    if (
      !Number.isFinite(hours) ||
      hours <= 0 ||
      remainingHours <= 0 ||
      hours > remainingHours
    ) {
      return;
    }
  
    this.isValidating = true;
  
    this.activityService
      .updateParticipationStatus(
        this.activityId,
        participant.id,
        'validated',
        hours,
        this.performanceInput[participant.id] ?? undefined,
        this.performanceCommentInput[participant.id] ?? '',
      )
      .subscribe({
        next: () => {
          console.log(
            'HORAS VALIDADAS:',
            participant.id,
            hours,
          );
  
          this.isValidating = false;
          this.showValidateConfirmation = false;
          this.studentToValidate = null;
          this.hoursToConfirm = null;
  
          this.loadRegisteredStudents();
        },
  
        error: (error: unknown) => {
          console.error(
            'ERROR VALIDANDO HORAS:',
            error,
          );
  
          this.isValidating = false;
          this.cdr.detectChanges();
        },
      });
  }


  /*
   * Carga todos los estudiantes y después
   * conserva únicamente los registrados
   * en esta actividad.
   */
  private loadRegisteredStudents(): void {
    this.activityService
      .getActivityParticipants(this.activityId)
      .subscribe({
        next: (participants) => {
          console.log('🔥 PARTICIPANTES DE LA ACTIVIDAD:', participants);
          this.participants = participants;
          this.registeredStudents = participants;
  
          this.registeredStudents.forEach((participant) => {
            if (
              participant.status === 'validated' &&
              this.validatedHoursInput[participant.id] === undefined
            ) {
              this.validatedHoursInput[participant.id] =
                participant.validatedHours ?? null;
            }
          });
  
          this.cdr.detectChanges();
        },
  
        error: (error: unknown) => {
          console.error(
            'ERROR CARGANDO PARTICIPANTES:',
            error,
          );
        },
      });
  }


  /*
   * Obtiene el estado actual de participación
   * del estudiante en ESTA actividad.
   */
  getParticipationStatus(
    participant: ActivityParticipant,
  ): string {
    return participant.status ?? 'registered';
  }


  /*
   * C3.2: folio de inscripción (AC-.../OP-...) del participante en esta
   * actividad. El backend M5.B lo embebe en el documento de participante;
   * leemos de forma tolerante (puede no venir) sin romper la interfaz.
   */
  getParticipantFolio(
    participant: ActivityParticipant,
  ): string | null {
    const folio = (participant as any)?.interactionFolio;
    return folio ? String(folio) : null;
  }

  getParticipantFolioType(
    participant: ActivityParticipant,
  ): 'AC' | 'OP' {
    const type = (participant as any)?.interactionType;
    return type === 'OP' ? 'OP' : 'AC';
  }

  copyParticipantFolio(folio: string, event?: Event): void {
    this.folioService.copyFolio(folio, event);
  }


  /*
   * Obtiene las horas que finalmente
   * fueron acreditadas al estudiante.
   */
  getValidatedHours(participant: ActivityParticipant): number {
    return Number(participant.validatedHours ?? 0);
  }

  getRemainingHours(participant: ActivityParticipant): number {
    if (participant.participantType === 'volunteer') {
      if (participant.volunteerType !== 'SERVICE_SOCIAL') {
        return 0;
      }
  
      return Math.max(
        Number(participant.valueInHours ?? 0) -
          Number(participant.validatedHours ?? 0),
        0,
      );
    }
  
    return Math.max(
      Number(participant.horasCubrir ?? 0) -
        Number(participant.horasAcreditadas ?? 0),
      0,
    );
  }


  /*
   * Nos permite saber si el administrador
   * puede acreditar horas.
   */
  canValidateHours(participant: ActivityParticipant): boolean {
    if (participant.participantType === 'volunteer') {
      // Los voluntarios generales no acreditan horas
      if (participant.volunteerType !== 'SERVICE_SOCIAL') {
        return false;
      }
    }
  
    const hours = this.validatedHoursInput[participant.id];
  
    return (
      this.getParticipationStatus(participant) === 'attended' &&
      hours !== null &&
      hours !== undefined &&
      Number.isFinite(Number(hours)) &&
      Number(hours) > 0 &&
      this.getRemainingHours(participant) > 0 &&
      Number(hours) <= this.getRemainingHours(participant)
    );
  }

  openValidateConfirmation(participant: ActivityParticipant): void {
    const hours = this.validatedHoursInput[participant.id];
  
    if (
      hours === null ||
      hours === undefined ||
      !Number.isFinite(Number(hours)) ||
      Number(hours) <= 0 ||
      Number(hours) > this.getRemainingHours(participant) ||
      this.getParticipationStatus(participant) !== 'attended'
    ) {
      return;
    }
  
    this.studentToValidate = participant as any;
    this.hoursToConfirm = Number(hours);
  
    this.performanceInput[participant.id] = null;
    this.performanceCommentInput[participant.id] = '';
  
    this.showValidateConfirmation = true;
  }
  
  getHoursLabel(hours: number): string {
    return hours === 1 ? 'hora' : 'horas';
  }
  
  closeValidateConfirmation(): void {
    if (this.isValidating) {
      return;
    }
  
    this.showValidateConfirmation = false;
    this.studentToValidate = null;
    this.hoursToConfirm = null;
  }
}